import React, { Fragment, useEffect, useState } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { setPageOptions } from 'reducers/layout';
import { setBreadcrumbs } from 'reducers/breadcrumbs';
import { openModal } from 'reducers/modals';
// components
import { FormattedMessage, useIntl } from 'react-intl';
import FeedOrderForm from './components/FeedOrderForm';
import SubmitOrderModal from './components/modals/SubmitOrderModal/SubmitOrderModal';
import OfflineSubmitAlert from 'components/DailyCheckups/modals/OfflineSubmitAlert/OfflineSubmitAlert';
// endpoints
import { createFeedOrder, getFeedOrderFarmBins, isRetriableFeedOrderError } from 'endpoints/feedOrders';
// utils
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import {
  buildPendingFeedOrder,
  deletePendingFeedOrder,
  getFeedOrderFormValues,
  getPendingFeedOrder,
  markFeedOrderSubmitted,
  savePendingFeedOrder,
} from 'utils/offlineFeedOrdersHelper';
import { withRouter } from 'react-router';
import mixpanel from 'mixpanel-browser';
import uuid from 'uuid';

// Outside the component: a new object on every render (e.g. on a network change) resets the bins in the form
const defaultValues = {
  delivery_date: null,
  notes: '',
  feed_order_bins_attributes: [{ bin_id: null, quantity: 0 }],
};

const CreateFeedOrder = ({
  pigGroup,
  setBreadcrumbs,
  setPageOptions,
  openModal,
  measurementSystem,
  isOnline,
  userId,
  companyId,
  router: { params: { farmId, groupId }, location: { query }, push },
}) => {
  const { formatMessage } = useIntl();
  const [binsOptions, setBinsOptions] = useState([]);
  // An order the server rejected is reopened with its values; it is replaced once the new one is placed
  const retryUuid = query?.retry;
  const [initialValues, setInitialValues] = useState(retryUuid ? null : defaultValues);

  useEffect(() => {
    setPageOptions({ backLink: `/feed-orders-farms/${farmId}` });
    if (pigGroup && pigGroup.farm) {

      getFeedOrderFarmBins(pigGroup.farm.id)
        .then((bins) => setBinsOptions(bins))
        .catch(toastResponseErrors);
    }
  }, [farmId, groupId, pigGroup]);

  useEffect(() => {
    if (!retryUuid) {
      setInitialValues(defaultValues);
      return;
    }
    getPendingFeedOrder(retryUuid)
      .then((order) => setInitialValues(order ? getFeedOrderFormValues(order) : defaultValues))
      .catch(() => setInitialValues(defaultValues));
  }, [retryUuid]);

  useEffect(() => {
    setBreadcrumbs([
      {
        label: <FormattedMessage id="general.feedOrders" />,
        path: '/feed-orders-farms',
      },
      {
        label: pigGroup?.farm?.name || '',
        path: `/feed-orders-farms/${farmId}`,
      },
      {
        label: pigGroup?.name || '',
        // No path - just display the group name without making it clickable
      },
      {
        label: <FormattedMessage id="general.createFeedOrder" />,
        useLabelAsMobileTitle: true,
      },
    ]);
  }, [pigGroup?.name, farmId, groupId]);

  const trackOrder = (action) => {
    mixpanel.track('Feature Used', {
      user_id: userId,
      feature_name: 'FeedOrder',
      action,
      pig_group_id: Number(groupId),
      timestamp: new Date().toISOString(),
    });
  };

  const dropRetriedOrder = () => (retryUuid ? deletePendingFeedOrder(retryUuid).catch(() => null) : Promise.resolve());

  const goToFarmOrders = () => push(`/feed-orders-farms/${farmId}/`);

  // Kept on the device and sent by NetworkManager once the connection is back (EPN-3082)
  const queueOrder = (resource) => {
    const order = buildPendingFeedOrder({ resource, pigGroup, bins: binsOptions, userId, companyId });
    return savePendingFeedOrder(order)
      .then(dropRetriedOrder)
      .then(() => {
        trackOrder('Queue Feed Order Offline');
        goToFarmOrders();
        openModal(
          <OfflineSubmitAlert
            messageId="component.modal.offlineSubmitAlert.feedOrderText"
            illustration="noConnection"
          />
        );
      })
      .catch(() => toastResponseErrors({ error: formatMessage({ id: 'component.feedOrder.offlineSaveError' }) }));
  };

  // The stored order is remembered on the device as the most recent one, so it can be checked offline
  const rememberSubmittedOrder = (resource, storedOrder) => {
    const order = buildPendingFeedOrder({ resource, pigGroup, bins: binsOptions, userId, companyId });
    return markFeedOrderSubmitted(order, storedOrder).catch(() => null);
  };

  const onSubmit = async (newOrderData) => {
    // Generated once per order, so every resend of it is recognized by the API
    const resource = { ...newOrderData, client_uuid: uuid() };
    if (!isOnline) return queueOrder(resource);

    try {
      const storedOrder = await createFeedOrder(groupId, resource);
      await rememberSubmittedOrder(resource, storedOrder);
      await dropRetriedOrder();
      goToFarmOrders();
      openModal(<SubmitOrderModal />);
    } catch (error) {
      // The request may not have reached the server; a resend with the same client_uuid is safe either way
      if (isRetriableFeedOrderError(error)) return queueOrder(resource);
      toastResponseErrors(error);
    }
    return undefined;
  };

  return (
    <Fragment>
      {initialValues && (
        <FeedOrderForm
          initialValues={initialValues}
          onSubmit={onSubmit}
          binsOptions={binsOptions}
          backLink={`/feed-orders-farms/${farmId}`}
          title={<FormattedMessage id="general.feedOrder" />}
          submitLabel={<FormattedMessage id="general.createFeedOrder" />}
          farmName={pigGroup?.farm?.name}
          groupName={pigGroup?.name}
          measurementSystem={measurementSystem}
          openModal={openModal}
        />
      )}
    </Fragment>
  );
};

CreateFeedOrder.propTypes = {
  setBreadcrumbs: T.func.isRequired,
  setPageOptions: T.func.isRequired,
  openModal: T.func.isRequired,
  pigGroup: T.object,
  measurementSystem: T.string,
  isOnline: T.bool.isRequired,
  userId: T.number,
  companyId: T.number,
  router: T.object.isRequired,
};

export default withRouter(connect(
  (state) => ({
    pigGroup: state.profiles.pigGroup.data,
    measurementSystem: state.auth.user.current_company.measurement_system,
    isOnline: state.network.isOnline,
    userId: state.auth.user.id,
    companyId: state.auth.user.current_company.id,
  }), {
    setPageOptions,
    setBreadcrumbs,
    openModal,
  }
)(CreateFeedOrder));
